import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import MainLayout from '../../components/layout/MainLayout';
import api from '../../services/api/api.service';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import AutoRefreshToggle, { AutoRefreshConfig } from '../../components/common/AutoRefreshToggle';

interface TransactionStats {
  name: string;
  calls: number;
  avgTime: number;
  errorRate: string;
  p95: number;
  p99: number;
  minTime: number;
  maxTime: number;
}

export default function TransactionsPage() {
  const { t } = useTranslation(['apm', 'common']);
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [transactions, setTransactions] = useState<TransactionStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<'calls' | 'avgTime' | 'errorRate'>('calls');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Time range and auto-refresh
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 60 * 60 * 1000, // Last hour
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '1h',
  });
  const [autoRefresh, setAutoRefresh] = useState<AutoRefreshConfig>({
    enabled: false,
    interval: 30000, // 30 seconds
  });

  useEffect(() => {
    setCurrentPage(1);
    loadTransactions();
  }, [selectedAppId, timeRange]);

  useEffect(() => {
    loadTransactions();
  }, [currentPage, pageSize]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh.enabled) return;

    const interval = setInterval(() => {
      if (timeRange.rangeType === 'preset' && timeRange.presetValue) {
        const endTime = Date.now();
        const ranges: Record<string, number> = {
          '15m': 15 * 60 * 1000,
          '1h': 60 * 60 * 1000,
          '6h': 6 * 60 * 60 * 1000,
          '24h': 24 * 60 * 60 * 1000,
          '7d': 7 * 24 * 60 * 60 * 1000,
        };
        const startTime = endTime - (ranges[timeRange.presetValue] || ranges['1h']);
        setTimeRange({ ...timeRange, startTime, endTime });
      } else {
        loadTransactions();
      }
    }, autoRefresh.interval);

    return () => clearInterval(interval);
  }, [autoRefresh, timeRange]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const { startTime, endTime } = timeRange;
      const appId = selectedAppId || 'all';

      // Use optimized backend endpoint for transaction aggregation
      const response = await api.get('/v1/apm/transactions', {
        params: {
          applicationId: appId,
          startTime,
          endTime,
          limit: 100,
          offset: 0
        }
      });

      if (response.success) {
        setTransactions(response.data);
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions
    .filter((transaction) => {
      if (!searchQuery) return true;
      return transaction.name.toLowerCase().includes(searchQuery.toLowerCase());
    })
    .sort((a, b) => {
      let aVal: number, bVal: number;
      if (sortBy === 'calls') {
        aVal = a.calls;
        bVal = b.calls;
      } else if (sortBy === 'avgTime') {
        aVal = a.avgTime;
        bVal = b.avgTime;
      } else {
        aVal = parseFloat(a.errorRate);
        bVal = parseFloat(b.errorRate);
      }
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

  const totalTransactions = filteredTransactions.length;
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSort = (column: 'calls' | 'avgTime' | 'errorRate') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (column: 'calls' | 'avgTime' | 'errorRate') => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };


  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('apm:transactions.title')}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t('apm:transactions.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        {/* Time Range and Auto Refresh Row */}
        <div className="flex flex-wrap items-center gap-4">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
          <AutoRefreshToggle
            value={autoRefresh}
            onChange={setAutoRefresh}
            onManualRefresh={loadTransactions}
          />
        </div>

        {/* Application and Search Row */}
        <div className="mb-6">
          <input
            type="text"
            placeholder={t('apm:transactions.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          />
        </div>
      </div>

      {/* Transactions Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : paginatedTransactions.length === 0 ? (
          <div className="flex justify-center items-center py-12 text-gray-500 dark:text-gray-400">
            {t('apm:transactions.noTransactions')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('apm:transactions.table.transactionName')}
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('calls')}
                  >
                    {t('apm:transactions.table.calls')} {getSortIcon('calls')}
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('avgTime')}
                  >
                    {t('apm:transactions.table.avgTime')} {getSortIcon('avgTime')}
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('errorRate')}
                  >
                    {t('apm:transactions.table.errorRate')} {getSortIcon('errorRate')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('apm:transactions.table.p95')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('apm:transactions.table.p99')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('apm:transactions.table.minMax')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-800">
                {paginatedTransactions.map((transaction, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {transaction.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                      {transaction.calls.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900 dark:text-white">
                      {transaction.avgTime}ms
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <span className={parseFloat(transaction.errorRate) > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}>
                        {transaction.errorRate}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                      {transaction.p95}ms
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                      {transaction.p99}ms
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                      {transaction.minTime}ms / {transaction.maxTime}ms
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {paginatedTransactions.length > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-800/50">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {t('apm:transactions.pagination.showing', {
                  from: ((currentPage - 1) * pageSize) + 1,
                  to: Math.min(currentPage * pageSize, totalTransactions),
                  total: totalTransactions
                })}
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="ml-4 rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
              >
                <option value={10}>{t('apm:transactions.pagination.perPage10')}</option>
                <option value={20}>{t('apm:transactions.pagination.perPage20')}</option>
                <option value={50}>{t('apm:transactions.pagination.perPage50')}</option>
              </select>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('apm:transactions.pagination.first')}
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('apm:transactions.pagination.previous')}
              </button>
              <span className="flex items-center px-3 text-sm text-gray-700 dark:text-gray-300">
                {t('apm:transactions.pagination.page', {
                  current: currentPage,
                  total: Math.ceil(totalTransactions / pageSize)
                })}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= Math.ceil(totalTransactions / pageSize)}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('apm:transactions.pagination.next')}
              </button>
              <button
                onClick={() => setCurrentPage(Math.ceil(totalTransactions / pageSize))}
                disabled={currentPage >= Math.ceil(totalTransactions / pageSize)}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('apm:transactions.pagination.last')}
              </button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
